import EmberService, { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import moment from 'moment';

import type { APIData, NpsFeedback, Service, TemplateResolve } from 'webapp-types';
import { RcItem } from 'webapp/enums/enums';
import { UserSettingsKey } from 'webapp/enums/user-settings';

import AccountPopupInactivityComponent from 'webapp/components/account/popup-inactivity';

import type { ConnectRequestData } from 'webapp/models-custom/connect-request';
import ConnectRequest from 'webapp/models-custom/connect-request';
import type { ConnectSelectionData } from 'webapp/models-custom/connect-selection';
import ConnectSelection from 'webapp/models-custom/connect-selection';
import ResolutionCenterItem from 'webapp/models-custom/resolutioncenter-item';

enum DataboardsNotificationsCategory {
	PUBLIC = 'public',
	AGENCY = 'agency',
	ISSUE = 'issue',
}

export interface SystemNotification {
	type: RcItem;
	title: string;
	text: string;
	displayResendLink?: boolean;
}

// Account-wide logic for resolution-center notifications & system status & the rest of notifications
export default class AppNotificationsService extends EmberService {
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('logger') declare loggerService: Service.Logger;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('flow/annual-plan') declare flowAnnualPlanService: Service.Flow.AnnualPlan;
	@service('flow/promotions')
	declare flowPromotionsService: Service.Flow.Promotions;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('automated-popup-queue')
	declare automatedPopupQueueService: Service.AutomatedPopupQueue;

	@tracked beNotifications: APIData.AppNotifications.ResolutionCenterMessage[] = [];
	@tracked systemStatus: APIData.AppNotifications.SystemStatus | null = null;
	@tracked skipVerifyNotification = false;
	@tracked skipInactiveUsersNotification = false;
	@tracked skipAllNotifications = false;
	@tracked skipLegacyMetricsNotification = false;

	// if value is greater than 0 we skip - numeric value used because of experiment 320223 to store timestamp of last check every month
	get skipYearlyUpgradeNotification(): number {
		return this.userSettingsService.skipYearlyUpgradeNotification;
	}

	set skipYearlyUpgradeNotification(value: number) {
		this.userSettingsService.saveLocal(UserSettingsKey.SKIP_YEARLY_UPGRADE_NOTIFICATION, value);
	}

	@cached
	get visibleNotifications(): ResolutionCenterItem[] {
		const filteredNotifications: (
				APIData.AppNotifications.ResolutionCenterMessage | SystemNotification
			)[] = [],
			currentUserId = this.currentUserService.numericId;

		// only show notifications that are related to current user or owner of specific object (e.g. board)
		this.beNotifications.forEach((notification) => {
			let hasAccess = false;
			if (
				notification.type === RcItem.BOARD_SHARE ||
				notification.type === RcItem.REPORT_SHARE
			) {
				hasAccess = true;
			} else if ([RcItem.CONNECT_SOURCE, RcItem.CHOOSE_SOURCE].includes(notification.type)) {
				hasAccess = !notification.boardIds;
				(notification.boardIds || []).forEach((boardId: number) => {
					const board = this.mmBoardsService.normalBoardModels.find(
						(item) => item.id === String(boardId),
					);
					if (
						board &&
						(board.createdById === currentUserId || board.ownerId === currentUserId)
					) {
						hasAccess = true;
					}
				});
			}
			if (
				hasAccess &&
				!filteredNotifications.find((item) => 'id' in item && item.id === notification.id)
			) {
				filteredNotifications.push(notification);
			}
		});

		if (!this.currentUserService.isViewer) {
			// include system alerts if there is any
			this.systemNotifications.forEach((notification) => {
				filteredNotifications.push(notification);
			});
		}

		return filteredNotifications.map((notification) => {
			return new ResolutionCenterItem(notification);
		});
	}

	get navigationMainDataboardsNotificationsCount(): number {
		return (
			this.transferedBoardPublicTemplateNotifications.length +
			this.transferedBoardAccountTemplateNotifications.length +
			this.mmBoardsService.normalBoardModels.filter((item) => item.isAnythingToSolve).length
		);
	}

	get navigationMainDataboardsNotificationsCountTooltip(): string {
		const count = {
			[DataboardsNotificationsCategory.PUBLIC]:
				this.transferedBoardPublicTemplateNotifications.length,
			[DataboardsNotificationsCategory.AGENCY]:
				this.transferedBoardAccountTemplateNotifications.length,
			[DataboardsNotificationsCategory.ISSUE]: this.mmBoardsService.normalBoardModels.filter(
				(item) => item.isAnythingToSolve,
			).length,
		};

		return Object.keys(count)
			.reduce((acc, k) => {
				const key = k as keyof typeof count;
				if (count[key]) {
					acc.push(this.getDataboardAttentionMessagePartial(key, count[key]));
				}
				return acc;
			}, [] as string[])
			.join(', ');
	}

	get navigationMainReportsNotificationsCount(): number {
		return (
			this.transferedReportAccountTemplateNotifications.length +
			this.transferedReportPublicTemplateNotifications.length
		);
	}

	get navigationMainSourcesNotificationsCount(): number {
		return this.mmSourcesService.myReconnectableSourcesCount + this.connectRequests.length;
	}

	// datasources that have been requested to connect and are waiting for connection or rejection
	@tracked connectRequests: ConnectRequest[] = [];

	// datasources that have been selected to connect on onboarding and are waiting for connection
	@tracked connectSelections: ConnectSelection[] = [];

	get transferedBoardPublicTemplateNotifications(): ResolutionCenterItem[] {
		return this.visibleNotifications
			.filter((item) => item.isTransferedBoardType)
			.filter((n) => n.params?.isPublicTemplate);
	}

	get transferedBoardAccountTemplateNotifications(): ResolutionCenterItem[] {
		return this.visibleNotifications
			.filter((item) => item.isTransferedBoardType)
			.filter((n) => !n.params?.isPublicTemplate);
	}

	get transferedReportPublicTemplateNotifications(): ResolutionCenterItem[] {
		return this.visibleNotifications
			.filter((item) => item.isTransferedReportType)
			.filter((n) => n.params?.isPublicTemplate);
	}

	get transferedReportAccountTemplateNotifications(): ResolutionCenterItem[] {
		return this.visibleNotifications
			.filter((item) => item.isTransferedReportType)
			.filter((n) => !n.params?.isPublicTemplate);
	}

	get sortedVisibleNotifications(): ResolutionCenterItem[] {
		return this.visibleNotifications
			.sort((notif1, notif2) => {
				// without timestamp first, then newest -> oldest
				if (notif1.timestamp === notif2.timestamp) {
					return 0;
				}
				if (!notif1.timestamp) {
					return -1;
				}
				if (!notif2.timestamp) {
					return 1;
				}
				if (notif1.timestamp > notif2.timestamp) {
					return -1;
				} else {
					return 1;
				}
			})
			.slice(0, 50);
	}

	private get systemNotifications(): SystemNotification[] {
		const notificationsArray: SystemNotification[] = [];
		// notification for expired trial
		if (this.showExpiredTrialWarning) {
			if (this.currentUserService.isAdmin) {
				notificationsArray.push({
					type: RcItem.OTHER,
					title: 'Your trial has expired. Data gathering is on hold. Enter billing info.',
					text: '<a href="/account/billing/plans">Select a plan</a>',
				});
			} else {
				notificationsArray.push({
					type: RcItem.OTHER,
					title: 'Your trial has expired. Data gathering is on hold. Contact your account administrator to renew subscription.',
					text: '',
				});
			}
		}
		if (this.showVerifyNotification) {
			notificationsArray.push({
				type: RcItem.OTHER,
				title: `Your email address ${this.currentUserService.model?.email} is not yet verified.`,
				displayResendLink: true,
				text: '',
			});
		}

		return notificationsArray;
	}

	get showSystemStatusNotification(): boolean {
		return !!this.systemStatus;
	}

	get showBillingNotification(): boolean {
		return (
			this.navigationService.initialRequestsDone && this.billingService.showAppNotification
		);
	}

	get showVerifyNotification(): boolean {
		return (
			!this.skipVerifyNotification &&
			this.currentUserService.isLoggedIn &&
			!this.currentUserService.isVerified
		);
	}

	get showCardExpirationNotification(): boolean {
		return this.billingService.isCardExpirationThisMonth;
	}

	get showFreeNotification(): boolean {
		return !!this.billingService.activePlan?.isFree;
	}

	get showYearlyUpgradeNotification(): boolean {
		const showYearlyUpgradeNotificationForTemplateSignups =
				!this.flowAnnualPlanService.isEligibleForRegularFlow &&
				this.billingService.showYearlyUpgradeNotification,
			showYearlyUpgradeNotificationForRegularSignups =
				this.flowAnnualPlanService.isEligibleToShowYearlyUpgradeNotification;

		return (
			(showYearlyUpgradeNotificationForTemplateSignups &&
				!this.skipYearlyUpgradeNotification) ||
			showYearlyUpgradeNotificationForRegularSignups
		);
	}

	get showTrialNotification(): boolean {
		// return this.billingService.isTrial
		// 	&& this.currentUserService.isAdmin;
		return false;
	}

	get showInactiveUsersNotification(): boolean {
		return (
			this.mmUsersService.inactiveInvitedUserRecordsForBanner.length > 0 &&
			!this.skipInactiveUsersNotification
		);
	}

	get showExpiredTrialWarning(): boolean {
		return this.billingService.isExpiredTrial;
	}

	get showDemoNotification(): boolean {
		return this.currentUserService.isDemo;
	}

	get showGrowth2024TrialNotification(): boolean {
		return (
			this.flowPromotionsService.showPromotions &&
			!!this.flowPromotionsService.promotions.growthFeatureTrialActivePromotion?.isCurrent
		);
	}

	get showGrowth2024DiscountNotification(): boolean {
		return (
			this.flowPromotionsService.showPromotions &&
			!!this.flowPromotionsService.promotions.growthPlanDiscountOptionPromotion?.isCurrent &&
			!this.flowPromotionsService.isGrowth2024BannerDiscountSnoozed
		);
	}

	get showLegacyMetricsNotification(): boolean {
		if (!this.navigationService.initialRequestsDone) return false;

		if (this.skipLegacyMetricsNotification) return false;

		if (this.userSettingsService.hasSeenLegacyMetricsBanner) return false;

		return this.mmSourcesService.sourceModels.some(
			(source) => source.hasLegacyMetricsToMigrate,
		);
	}

	get showAppNotifications(): boolean {
		return (
			!this.skipAllNotifications &&
			(this.showBillingNotification ||
				this.showVerifyNotification ||
				this.showFreeNotification ||
				this.showSystemStatusNotification ||
				this.showCardExpirationNotification ||
				this.showYearlyUpgradeNotification ||
				this.showTrialNotification ||
				this.showInactiveUsersNotification ||
				this.showDemoNotification ||
				this.showGrowth2024TrialNotification ||
				this.showGrowth2024DiscountNotification ||
				this.showLegacyMetricsNotification)
		);
	}

	setBeNotifications(data: APIData.AppNotifications.ResolutionCenterMessage[] = []): void {
		// filtering notifications types that are relevant to viewer
		if (this.currentUserService.model?.isViewer) {
			const notificationTypesAccessibleByViewer = [
				RcItem.CONNECT_SOURCE,
				RcItem.CHOOSE_SOURCE,
			];

			data = data.filter((notif: APIData.AppNotifications.ResolutionCenterMessage) =>
				notificationTypesAccessibleByViewer.includes(notif.type),
			);
		}

		this.beNotifications = data || [];
		this.checkShowInactivityPopup();
	}

	getResolutionCenterMessages(): Promise<unknown> {
		return this.requestService
			.api<APIData.AppNotifications.ResolutionCenterResponse>('GET', '/resolution-center')
			.then((reply) => {
				// must not process before source types are loaded
				return new Promise((resolve, reject) => {
					this.mmSourceTypesService.requestSourceTypes().then(() => {
						resolve(reply);
					}, reject);
				});
			})
			.then((reply) => {
				this.setBeNotifications(
					(reply as APIData.AppNotifications.ResolutionCenterResponse).messages || [],
				);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	getConnectRequests() {
		return this.requestService
			.api<APIData.AppNotifications.ConnectRequestsResponse>('GET', '/connect/by-request')
			.then((reply) => {
				// reply = window.dbx_mock['GET /d/connect/by-request'];
				this.updateConnectRequests(reply.connectRequests);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	getConnectSelections() {
		return this.requestService
			.api<APIData.AppNotifications.ConnectSelectionResponse>('GET', '/connect/selection')
			.then((reply) => {
				// reply = { "selectedConnections": [ { "dataSourceTypeId": 1}, { "dataSourceTypeId": 239} ] };
				this.updateConnectSelections(reply.selectedConnections);
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	updateConnectRequests(connectRequestDataArray: ConnectRequestData[]) {
		const connectRequestModels = connectRequestDataArray.map((crData) => {
			return new ConnectRequest(crData);
		});
		this.connectRequests = connectRequestModels;
	}

	updateConnectSelections(connectSelectionDataArray: ConnectSelectionData[]) {
		const connectSelectionModels = connectSelectionDataArray.map((csData) => {
			return new ConnectSelection(csData);
		});
		this.connectSelections = connectSelectionModels;
	}

	getSystemStatus() {
		return this.requestService
			.api<APIData.AppNotifications.SystemStatusResponse>('GET', '/system-status')
			.then((response) => {
				this.systemStatus = response.status;
			})
			.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
	}

	getNpsFeedback(): Promise<void> {
		return this.requestService.api<NpsFeedback>('GET', '/tracking/feedback').then(
			(data) => {
				this.currentUserService.npsFeedback = data; // { nextTimestamp: 1658552560, maxScoreCount: 0};

				const npsFeedback = this.currentUserService.npsFeedback,
					isEligibleForNpsFormToBeShown =
						npsFeedback &&
						!!npsFeedback.nextTimestamp && // conditions when timestamp is returned: https://app.asana.com/0/1199660006694090/1202567477409654/f
						npsFeedback.nextTimestamp <= moment().unix();

				this.currentUserService.isEligibleForNpsFormToBeShown =
					isEligibleForNpsFormToBeShown;
			},
			(error) => {
				if (error?.code) {
					this.loggerService.logError('[NPS Feedback] Problem getting data.', {
						error: JSON.stringify(error),
					});
				}
			},
		);
	}

	resolveTemplateNotificationFromResolutionCenter(
		isAccept: boolean,
		messageId: number,
		data?: TemplateResolve.NormalizedResolvePayload,
	): Promise<APIData.AppNotifications.ResolveTemplateNotificationFromResolutionCenterResponse | void> {
		if (!messageId) {
			this.infolayerService.error('No messageId');
			return Promise.reject();
		}

		let request;
		if (isAccept) {
			request =
				this.requestService.apiWithAsyncReply<APIData.AppNotifications.ResolveTemplateNotificationFromResolutionCenterResponse>(
					'POST',
					`/resolution-center/${messageId}`,
					data ?? { dataSourceTypeMap: {} },
				);
		} else {
			request = this.requestService.api('DELETE', `/resolution-center/${messageId}`);
		}

		return request.then((reply) => {
			this.beNotifications = this.beNotifications.filter((n) => n.id !== messageId);

			return reply;
		});
	}

	private hasOpenedInactivityPopup = false;

	private getDataboardAttentionMessagePartial(
		key: DataboardsNotificationsCategory,
		count: number,
	): string {
		let message = '';
		if (count) {
			switch (key) {
				case DataboardsNotificationsCategory.PUBLIC:
					message =
						count + ' template' + (count === 1 ? '' : 's') + ' waiting to be used';
					break;
				case DataboardsNotificationsCategory.AGENCY:
					message = count + ' dashboard' + (count === 1 ? '' : 's') + ' to be accepted';
					break;
				case DataboardsNotificationsCategory.ISSUE:
					message = count + ' dashboard' + (count === 1 ? '' : 's') + ' with issues';
					break;
			}
		}

		return message;
	}

	private createInactivityPopup() {
		return this.popupService.createModal(AccountPopupInactivityComponent, {
			popupClass: 'popup-type-inactivity title-bigger',
			closeOnOverlayClick: false,
		});
	}

	private get hasInactivityNotification(): boolean {
		return this.beNotifications.some((n) => n.type === RcItem.INACTIVITY_WARNING);
	}

	// Goes through the automated popup queue: opened directly, the next onboarding popup the
	// queue opens closes it, and closing it resolves the message before the user has read it.
	private checkShowInactivityPopup(): void {
		const inactivityNotif = this.beNotifications.find(
			(n) => n.type === RcItem.INACTIVITY_WARNING,
		);

		if (this.hasOpenedInactivityPopup || !inactivityNotif) {
			return;
		}

		this.automatedPopupQueueService.queuePopup({
			popup: this.createInactivityPopup(),
			popupData: { msgId: inactivityNotif.id },
			checkAddConditions: () => {
				return !this.hasOpenedInactivityPopup && this.hasInactivityNotification;
			},
			checkStartConditions: () => {
				// The queue opens the popup right after this returns true.
				this.hasOpenedInactivityPopup = true;
				return true;
			},
			skipCooldown: true,
			queueFirst: true,
		});
		this.automatedPopupQueueService.checkToOpenPopup();
	}
}

declare module '@ember/service' {
	interface Registry {
		'app-notifications': AppNotificationsService;
	}
}
